import { loginWithToken } from '~/api/auth';
import AuthenticationStore from '~/auth/authenticationStore';
import { trackAnalyticsEvent } from '~/common/analytics/analyticsClient';

import {
  getDecodedUserInfoFromLoginToken,
  resolvePasswordResetHandoff,
  workspacePathAfterLogin,
  type LoginHandoffFeatureFlags,
} from './utils';

interface PasswordResetResponse {
  msg?: string;
  token?: string;
  first_login?: boolean;
  user_id?: string;
  client_id?: string;
}

interface LoginIdentity {
  client?: string;
  client_id?: string | number;
  user_id?: string | number;
}

function trackHandoff(
  event: string,
  response: PasswordResetResponse,
  identity: LoginIdentity | null,
  formatDate: (date: Date, format: 'utc') => string,
) {
  try {
    const clientId = response.client_id || identity?.client_id;
    const userId = response.user_id || identity?.user_id;
    if (!clientId && !userId) return;

    trackAnalyticsEvent(event, {
      groupId: clientId,
      userId: `${userId}_${clientId}`,
      firstLoginDate: formatDate(new Date(), 'utc'),
    });
  } catch {
    return;
  }
}

function identityFromRedirect(redirectUrl: string) {
  try {
    return getDecodedUserInfoFromLoginToken(redirectUrl);
  } catch {
    return null;
  }
}

export async function completePasswordResetHandoff(
  response: PasswordResetResponse,
  {
    featureFlags,
    event,
    formatDate,
  }: {
    featureFlags: LoginHandoffFeatureFlags;
    event: string;
    formatDate: (date: Date, format: 'utc') => string;
  },
) {
  const handoff = resolvePasswordResetHandoff(response);

  if (handoff.token) {
    const data = await loginWithToken({ token: handoff.token });
    const authStore = AuthenticationStore();
    if (data.access_token && data.refresh_token) {
      authStore.saveAuthentication(data.access_token, data.refresh_token);
    }
    if (data.user_id && data.client_id) {
      authStore.removeInactiveTimer();
      authStore.saveJwtTokenCreationTime();
    }

    const identity = getDecodedUserInfoFromLoginToken(handoff.token);
    trackHandoff(event, response, identity, formatDate);
    window.setTimeout(() => {
      window.location.href = workspacePathAfterLogin({
        client: identity.client,
        isFirstLogin: handoff.isFirstLogin,
        featureFlags,
      });
    }, 1000);
    return;
  }

  if (!handoff.redirectUrl) return;

  trackHandoff(
    event,
    response,
    identityFromRedirect(handoff.redirectUrl),
    formatDate,
  );
  window.setTimeout(() => {
    window.location.href = handoff.redirectUrl;
  }, 1000);
}
